import {
  Box, Container, Heading, Button, Card, CardBody, VStack, HStack,
  Text, Badge, Spinner, Alert, AlertIcon, useToast,
  IconButton, Input, Textarea, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, FormControl, FormLabel, Flex, Tooltip, useClipboard,
  SimpleGrid, Stat, StatLabel, StatNumber, Divider, ButtonGroup
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  getListaCompraDetalhes,
  aprovarRespostaFornecedor,
  finalizarAprovacaoLista,
  aceitarFuroEstoqueItem,
  desfazerAceiteFuroEstoqueItem,
  excluirItemCotacao,
  desfazerExclusaoItemCotacao,
  tratarDivergencia,
  aprovarAjusteDivergencia,
  desfazerAprovacaoResposta,
  marcarFuroPosConfirmacaoItem,
} from "../services/api";
import {
  FiCheck, FiX, FiArrowLeft, FiCheckCircle, FiAlertTriangle,
  FiThumbsUp, FiThumbsDown, FiPackage, FiDollarSign, FiTag,
  FiRotateCcw, FiSlash, FiLink,
} from "react-icons/fi";
import { CopyIcon } from "@chakra-ui/icons";
import ProdutoImagem from "../components/ProdutoImagem";

const TIPOS_AJUSTE = ['valor_diferente', 'quantidade_diferente'];

const TIPOS_DIVERGENCIA_LABELS = {
  marca_diferente: 'Marca diferente',
  valor_diferente: 'Preço diferente',
  quantidade_diferente: 'Quantidade diferente',
  imagem_incorreta: 'Imagem incorreta',
  codigo_diferente: 'Código diferente',
  descricao_incorreta: 'Descrição incorreta',
  outro: 'Outro',
};

const ESTADO_LABELS = {
  AGUARDANDO_RESPOSTA:           { label: 'Aguardando resposta',           color: 'gray' },
  AJUSTE_PENDENTE:               { label: 'Ajuste pendente',                color: 'orange' },
  PENDENTE_APROVACAO:            { label: 'Aguardando sua decisão',         color: 'yellow' },
  APROVADO:                      { label: 'Aprovado',                       color: 'green' },
  REJEITADO:                     { label: 'Rejeitado',                      color: 'red' },
  FURO_SEM_ALTERNATIVA:          { label: 'Sem estoque',                    color: 'orange' },
  FURO_COM_ALTERNATIVA_APROVADA: { label: 'Sem estoque + alternativa OK',   color: 'green' },
  FURO_SEM_ALTERNATIVA_ACEITA:   { label: 'Furo aceito',                    color: 'orange' },
};

const ESTADOS_BLOQUEADORES = ['AGUARDANDO_RESPOSTA', 'AJUSTE_PENDENTE', 'PENDENTE_APROVACAO'];

// Item tem alternativa quando ha pelo menos uma resposta diferente da
// "resposta original" (mesmo codigo_fornecedor + mesma marca solicitada).
// Confirmacao do item original (com ou sem ajuste) NAO conta como alternativa.
const temAlternativaOferecida = (item) => {
  const respostas = item.respostas || [];
  if (respostas.length === 0) return false;
  const original = respostas.find(r =>
    r.codigo_fornecedor === (item.codigo_fornecedor || item.codigo_global) &&
    (r.marca_oferecida || '').toLowerCase() === (item.marca_solicitada || '(sem marca)').toLowerCase()
  );
  return respostas.some(r => r.id !== original?.id);
};

// Resposta de "confirmação do item original": código + marca batem com o
// solicitado. É o que o botão verde ✓ do fornecedor cria — distingue uma
// confirmação formal de uma alternativa ou de um ajuste ainda não confirmado.
const getRespostaOriginal = (item) => {
  const respostas = item.respostas || [];
  return respostas.find(r =>
    r.codigo_fornecedor === (item.codigo_fornecedor || item.codigo_global) &&
    (r.marca_oferecida || '').toLowerCase() === (item.marca_solicitada || '(sem marca)').toLowerCase()
  ) || null;
};

// Ajuste de qtd/preço só é aprovável pelo comprador depois que o fornecedor
// confirma o item. Sem confirmação, o fornecedor ainda está preenchendo.
const ajusteAprovavel = (item) =>
  (item.divergencias || []).some(d => TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente')
  && !!getRespostaOriginal(item);

const FILTROS = [
  { key: 'todos',        label: 'Todos' },
  { key: 'pendentes',    label: 'Pendentes' },
  { key: 'ajustes',      label: 'Com ajuste' },
  { key: 'alternativas', label: 'Com alternativa' },
  { key: 'divergencias', label: 'Divergências' },
  { key: 'furos',        label: 'Sem estoque' },
  { key: 'aprovados',    label: 'Aprovados' },
];

const formatarMoedaDecimal = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
};

// ─── Link da lista enviada (copia/abre) ────────────────────────────────
function LinkListaEnviada({ url }) {
  const { onCopy, hasCopied } = useClipboard(url || '');
  if (!url) return null;
  return (
    <Box bg="whiteAlpha.300" p={3} borderRadius="md" mt={2}>
      <Text fontSize="xs" fontWeight="bold" mb={1}>
        <FiLink style={{ display: 'inline', marginRight: 4 }} />
        Link enviado ao fornecedor:
      </Text>
      <HStack spacing={2}>
        <Text fontSize="xs" wordBreak="break-all" flex={1}>{url}</Text>
        <Button size="xs" leftIcon={<CopyIcon />} colorScheme={hasCopied ? "green" : "whiteAlpha"}
          variant="solid" onClick={onCopy}>
          {hasCopied ? "Copiado!" : "Copiar"}
        </Button>
        <Button size="xs" as="a" href={url} target="_blank" rel="noopener noreferrer"
          colorScheme="whiteAlpha" variant="solid">
          Abrir
        </Button>
      </HStack>
    </Box>
  );
}

// ─── Card de ajuste de qtd/preço (mais relevante visualmente) ──────────
function AjusteCard({ item, ajustes, onAceitar, onRejeitar, processando }) {
  const ajusteQtd = ajustes.find(d => d.tipo === 'quantidade_diferente');
  const ajustePreco = ajustes.find(d => d.tipo === 'valor_diferente');

  const novaQtd = ajusteQtd?.quantidade_informada ?? item.quantidade_solicitada;
  const novoPreco = ajustePreco?.preco_unitario_informado ?? item.preco_referencia;

  const precoRef = parseFloat(item.preco_referencia || 0);
  const novoPrecoNum = parseFloat(novoPreco || 0);
  const variacaoPrecoPct = precoRef > 0 ? ((novoPrecoNum - precoRef) / precoRef) * 100 : 0;

  const valorTotalOriginal = precoRef * (parseInt(item.quantidade_solicitada, 10) || 0);
  const valorTotalNovo = novoPrecoNum * (parseInt(novaQtd, 10) || 0);
  const diff = valorTotalNovo - valorTotalOriginal;

  // Texto do botão consolidado: variação de preço quando relevante, aposta na info mais importante
  const partes = [];
  if (ajustePreco && Math.abs(variacaoPrecoPct) >= 0.05) {
    partes.push(`${variacaoPrecoPct > 0 ? '+' : ''}${variacaoPrecoPct.toFixed(1).replace('.', ',')}%`);
  }
  if (ajusteQtd) {
    partes.push(`qtd ${ajusteQtd.quantidade_informada}`);
  }
  const labelBotao = partes.length > 0 ? `Aprovar (${partes.join(' • ')})` : 'Aprovar ajuste';

  return (
    <Box
      borderRadius="md"
      bg="orange.50"
      borderWidth="2px"
      borderColor="orange.400"
      p={3}
    >
      <HStack mb={2} spacing={2}>
        <FiAlertTriangle color="#EA580C" />
        <Text fontSize="sm" fontWeight="bold" color="orange.700">
          AJUSTE PROPOSTO PELO FORNECEDOR
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={3}>
        {ajusteQtd && (
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">QUANTIDADE</Text>
            <HStack>
              <Text fontSize="xs" color="gray.400" textDecoration="line-through">
                {item.quantidade_solicitada}
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="orange.700">
                → {ajusteQtd.quantidade_informada}
              </Text>
            </HStack>
          </Box>
        )}
        {ajustePreco && (
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">PREÇO UNITÁRIO</Text>
            <HStack>
              <Text fontSize="xs" color="gray.400" textDecoration="line-through">
                {formatarMoedaDecimal(item.preco_referencia)}
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="orange.700">
                → {formatarMoedaDecimal(ajustePreco.preco_unitario_informado)}
              </Text>
              <Badge fontSize="2xs" colorScheme={variacaoPrecoPct > 0 ? 'red' : 'green'}>
                {variacaoPrecoPct > 0 ? '+' : ''}{variacaoPrecoPct.toFixed(1)}%
              </Badge>
            </HStack>
          </Box>
        )}
      </SimpleGrid>

      <Box bg="white" borderRadius="sm" p={2} mb={3}>
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">Total se aprovado:</Text>
          <Text fontSize="md" fontWeight="bold" color="orange.700">
            {formatarMoedaDecimal(valorTotalNovo)}
            {diff !== 0 && (
              <Text as="span" fontSize="xs" color={diff > 0 ? 'red.600' : 'green.600'} ml={2}>
                ({diff > 0 ? '+' : ''}{formatarMoedaDecimal(diff)})
              </Text>
            )}
          </Text>
        </HStack>
      </Box>

      <HStack spacing={2}>
        <Button
          colorScheme="green" leftIcon={<FiCheck />} onClick={onAceitar}
          isLoading={processando} flex={1}
        >
          {labelBotao}
        </Button>
        <Button
          variant="outline" colorScheme="red" leftIcon={<FiX />} onClick={onRejeitar}
          isLoading={processando}
        >
          Rejeitar
        </Button>
      </HStack>
    </Box>
  );
}

// ─── Card de divergência cadastral ─────────────────────────────────────
function DivergenciaCadastralCard({ divergencia, item, onTratar }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [acao, setAcao] = useState(null);
  const [obs, setObs] = useState('');
  const [processando, setProcessando] = useState(false);

  const tipoLabel = TIPOS_DIVERGENCIA_LABELS[divergencia.tipo] || divergencia.tipo;
  const isPendente = divergencia.status === 'pendente';

  const codigoOriginal = item?.codigo_fornecedor || item?.codigo_global || '';
  const marcaOriginal = item?.marca_solicitada || '';
  const codigoMudou = !!divergencia.codigo_fornecedor_informado
    && divergencia.codigo_fornecedor_informado !== codigoOriginal;
  const marcaMudou = !!divergencia.marca_informada
    && divergencia.marca_informada !== marcaOriginal;

  const handleAbrir = (a) => { setAcao(a); setObs(''); onOpen(); };
  const handleConfirmar = async () => {
    setProcessando(true);
    await onTratar(divergencia.id, acao, obs || null);
    setProcessando(false);
    onClose();
  };

  return (
    <>
      <Box
        borderRadius="md"
        bg={isPendente ? 'yellow.50' : divergencia.status === 'reconhecida' ? 'green.50' : 'gray.50'}
        borderWidth="2px"
        borderColor={isPendente ? 'yellow.400' : divergencia.status === 'reconhecida' ? 'green.300' : 'gray.300'}
        p={3}
      >
        <HStack mb={2} spacing={2}>
          <FiAlertTriangle color={isPendente ? "#CA8A04" : "#6B7280"} />
          <Text fontSize="sm" fontWeight="bold" color={isPendente ? "yellow.800" : "gray.700"}>
            DIVERGÊNCIA: {tipoLabel.toUpperCase()}
          </Text>
          <Badge colorScheme={isPendente ? 'yellow' : divergencia.status === 'reconhecida' ? 'green' : 'gray'}>
            {divergencia.status}
          </Badge>
        </HStack>

        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={2}>
          {divergencia.codigo_fornecedor_informado && (
            <Box bg="white" borderRadius="sm" p={2}>
              <Text fontSize="2xs" color="gray.500" fontWeight="bold">CÓDIGO</Text>
              <HStack>
                {codigoMudou && codigoOriginal && (
                  <Text fontSize="xs" color="gray.400" textDecoration="line-through">{codigoOriginal}</Text>
                )}
                <Text fontSize="sm" fontWeight="bold" color={codigoMudou ? 'orange.600' : 'gray.800'}>
                  {codigoMudou && '→ '}{divergencia.codigo_fornecedor_informado}
                </Text>
              </HStack>
            </Box>
          )}
          {divergencia.marca_informada && (
            <Box bg="white" borderRadius="sm" p={2}>
              <Text fontSize="2xs" color="gray.500" fontWeight="bold">MARCA</Text>
              <HStack>
                {marcaMudou && marcaOriginal && (
                  <Text fontSize="xs" color="gray.400" textDecoration="line-through">{marcaOriginal}</Text>
                )}
                <Text fontSize="sm" fontWeight="bold" color={marcaMudou ? 'orange.600' : 'gray.800'}>
                  {marcaMudou && '→ '}{divergencia.marca_informada}
                </Text>
              </HStack>
            </Box>
          )}
        </SimpleGrid>

        {divergencia.descricao && (
          <Text fontSize="sm" color="gray.700" mb={2} bg="white" p={2} borderRadius="sm">
            {divergencia.descricao}
          </Text>
        )}

        {divergencia.observacoes_tratamento && (
          <Text fontSize="xs" color="gray.600" fontStyle="italic" mb={2}>
            Tratamento: {divergencia.observacoes_tratamento}
          </Text>
        )}

        {isPendente && (
          <HStack spacing={2}>
            <Button size="sm" colorScheme="green" leftIcon={<FiThumbsUp />} onClick={() => handleAbrir('reconhecer')} flex={1}>
              Reconhecer
            </Button>
            <Button size="sm" variant="outline" colorScheme="gray" leftIcon={<FiThumbsDown />} onClick={() => handleAbrir('dispensar')}>
              Dispensar
            </Button>
          </HStack>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {acao === 'reconhecer' ? 'Reconhecer divergência' : 'Dispensar divergência'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="xs" fontWeight="bold">{tipoLabel}</Text>
                {divergencia.descricao && <Text fontSize="sm">{divergencia.descricao}</Text>}
              </Box>
              <FormControl>
                <FormLabel fontSize="sm">Observações (opcional)</FormLabel>
                <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3}
                  placeholder={acao === 'reconhecer' ? 'Como será tratado?' : 'Motivo da dispensa'} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
            <Button colorScheme={acao === 'reconhecer' ? 'green' : 'gray'}
              onClick={handleConfirmar} isLoading={processando}>
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ─── Card de resposta (confirmação original ou alternativa) ────────────
function RespostaCard({ resposta, item, ehOriginal, onAprovar, onRejeitar, onRefresh,
                        onDesfazerAprovacao, onMarcarFuroPos }) {
  const aprovarMd = useDisclosure();
  const rejeitarMd = useDisclosure();
  const furoMd = useDisclosure();
  const [qtdAp, setQtdAp] = useState(resposta.quantidade_disponivel);
  const [obs, setObs] = useState('');
  const [obsFuro, setObsFuro] = useState('');
  const [proc, setProc] = useState(false);
  const [procPos, setProcPos] = useState(false);
  const toast = useToast();

  const variacaoPreco = item?.preco_referencia
    ? (((parseFloat(resposta.preco_unitario) - parseFloat(item.preco_referencia)) / parseFloat(item.preco_referencia)) * 100)
    : 0;
  const temVarPrecoSig = Math.abs(variacaoPreco) >= 2.5;

  const handleAprovar = async () => {
    setProc(true);
    await onAprovar(resposta.id, qtdAp, obs);
    setProc(false);
    aprovarMd.onClose();
    onRefresh();
  };

  const handleRejeitar = async () => {
    if (!obs?.trim()) {
      toast({ title: "Informe o motivo da rejeição", status: "warning", duration: 2500 });
      return;
    }
    setProc(true);
    await onRejeitar(resposta.id, obs);
    setProc(false);
    rejeitarMd.onClose();
    onRefresh();
  };

  const handleDesfazer = async () => {
    setProcPos(true);
    const r = await onDesfazerAprovacao(resposta.id);
    setProcPos(false);
    if (r) {
      toast({ title: "Aprovação desfeita", status: "info", duration: 2000 });
      onRefresh();
    } else {
      toast({ title: "Erro ao desfazer aprovação", status: "error", duration: 2500 });
    }
  };

  const handleMarcarFuro = async () => {
    setProcPos(true);
    const r = await onMarcarFuroPos(item.id, obsFuro || null);
    setProcPos(false);
    if (r) {
      toast({ title: "Furo registrado", status: "warning", duration: 2500 });
      furoMd.onClose();
      setObsFuro('');
      onRefresh();
    } else {
      toast({ title: "Erro ao registrar furo", status: "error", duration: 2500 });
    }
  };

  const aprovado = resposta.status_aprovacao === 'aprovado';
  const rejeitado = resposta.status_aprovacao === 'rejeitado';

  const corFundo = ehOriginal
    ? (aprovado ? 'green.50' : rejeitado ? 'red.50' : 'green.50')
    : (aprovado ? 'green.50' : rejeitado ? 'red.50' : 'blue.50');

  const corBorda = ehOriginal
    ? (aprovado ? 'green.400' : rejeitado ? 'red.300' : 'green.300')
    : (aprovado ? 'green.400' : rejeitado ? 'red.300' : 'blue.400');

  return (
    <>
      <Box borderRadius="md" bg={corFundo} borderWidth="2px" borderColor={corBorda} p={3}>
        <HStack mb={2} spacing={2}>
          {ehOriginal ? (
            <>
              <FiCheckCircle color="#16A34A" />
              <Text fontSize="sm" fontWeight="bold" color="green.800">
                FORNECEDOR CONFIRMOU O ITEM
              </Text>
            </>
          ) : (
            <>
              <FiPackage color="#1E40AF" />
              <Text fontSize="sm" fontWeight="bold" color="blue.800">
                ALTERNATIVA OFERECIDA
              </Text>
            </>
          )}
          {aprovado && <Badge colorScheme="green">aprovada</Badge>}
          {rejeitado && <Badge colorScheme="red">rejeitada</Badge>}
        </HStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={2}>
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">MARCA</Text>
            <Text fontSize="sm" fontWeight="bold">{resposta.marca_oferecida}</Text>
          </Box>
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">CÓDIGO</Text>
            <Text fontSize="sm" fontWeight="bold">{resposta.codigo_fornecedor}</Text>
          </Box>
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">QUANTIDADE</Text>
            <Text fontSize="sm" fontWeight="bold">{resposta.quantidade_disponivel} un.</Text>
          </Box>
          <Box bg="white" borderRadius="sm" p={2}>
            <Text fontSize="2xs" color="gray.500" fontWeight="bold">PREÇO UNIT.</Text>
            <HStack spacing={1}>
              <Text fontSize="sm" fontWeight="bold" color={temVarPrecoSig ? (variacaoPreco > 0 ? 'red.600' : 'green.600') : 'gray.800'}>
                {formatarMoedaDecimal(resposta.preco_unitario)}
              </Text>
              {temVarPrecoSig && (
                <Badge fontSize="2xs" colorScheme={variacaoPreco > 0 ? 'red' : 'green'}>
                  {variacaoPreco > 0 ? '+' : ''}{variacaoPreco.toFixed(1)}%
                </Badge>
              )}
            </HStack>
          </Box>
        </SimpleGrid>

        <Box bg="white" borderRadius="sm" p={2} mb={2}>
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.600">Total:</Text>
            <Text fontSize="md" fontWeight="bold" color="#01695E">
              {formatarMoedaDecimal(resposta.preco_total)}
            </Text>
          </HStack>
        </Box>

        {resposta.observacoes_fornecedor && (
          <Text fontSize="xs" color="blue.700" bg="blue.50" p={2} borderRadius="sm" mb={2}>
            {resposta.observacoes_fornecedor}
          </Text>
        )}

        {aprovado && (
          <>
            <Box bg="green.100" p={2} borderRadius="sm" mb={2}>
              <Text fontSize="xs" color="green.800" fontWeight="bold">
                ✓ Aprovado: {resposta.quantidade_aprovada} unidades
              </Text>
              {resposta.observacoes_aprovacao && (
                <Text fontSize="xs" color="green.800">{resposta.observacoes_aprovacao}</Text>
              )}
            </Box>
            <HStack spacing={2}>
              <Button size="xs" variant="outline" colorScheme="gray" leftIcon={<FiRotateCcw />}
                onClick={handleDesfazer} isLoading={procPos}>
                Desfazer aprovação
              </Button>
              <Button size="xs" variant="outline" colorScheme="red" leftIcon={<FiSlash />}
                onClick={furoMd.onOpen} isLoading={procPos}>
                Marcar furo (fornecedor avisou)
              </Button>
            </HStack>
          </>
        )}

        {rejeitado && (
          <Box bg="red.100" p={2} borderRadius="sm" mb={2}>
            <Text fontSize="xs" color="red.800" fontWeight="bold">✗ {resposta.observacoes_aprovacao}</Text>
          </Box>
        )}

        {!aprovado && !rejeitado && (
          <HStack spacing={2}>
            <Button size="sm" colorScheme="green" leftIcon={<FiCheck />} onClick={aprovarMd.onOpen} flex={1}>
              Aprovar
            </Button>
            <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FiX />} onClick={rejeitarMd.onOpen}>
              Rejeitar
            </Button>
          </HStack>
        )}
      </Box>

      <Modal isOpen={aprovarMd.isOpen} onClose={aprovarMd.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Aprovar resposta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Quantidade aprovada</FormLabel>
                <Input type="number" value={qtdAp} onChange={(e) => setQtdAp(e.target.value)} max={resposta.quantidade_disponivel} />
                <Text fontSize="xs" color="gray.500" mt={1}>Máximo: {resposta.quantidade_disponivel}</Text>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Observações (opcional)</FormLabel>
                <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={aprovarMd.onClose}>Cancelar</Button>
            <Button colorScheme="green" onClick={handleAprovar} isLoading={proc}>Confirmar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={rejeitarMd.isOpen} onClose={rejeitarMd.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rejeitar resposta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Motivo</FormLabel>
              <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3}
                placeholder="Ex.: Marca não aceita, preço acima do limite" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={rejeitarMd.onClose}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleRejeitar} isLoading={proc}>Confirmar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={furoMd.isOpen} onClose={furoMd.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.700">Registrar furo após confirmação</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Esta ação reverte a aprovação deste item e marca como sem estoque.
                Use quando o fornecedor avisar (por fora) que furou após a confirmação.
              </Alert>
              <FormControl>
                <FormLabel fontSize="sm">Observações (opcional)</FormLabel>
                <Textarea value={obsFuro} onChange={(e) => setObsFuro(e.target.value)} rows={3}
                  placeholder="Ex.: Fornecedor ligou avisando que vendeu o último em outra venda" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={furoMd.onClose}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleMarcarFuro} isLoading={procPos}>
              Confirmar furo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ─── Card por item ──────────────────────────────────────────────────────
function ItemCard({ item, fornecedor, onAprovarResposta, onRejeitarResposta, onRefresh,
                    onAceitarFuro, onDesfazerAceiteFuro, onTratarDivergencia, onAprovarAjuste,
                    onDesfazerAprovacao, onMarcarFuroPos, onExcluirCotacao, onDesfazerExclusaoCotacao }) {
  const imageMd = useDisclosure();
  const [processandoAjuste, setProcessandoAjuste] = useState(false);
  const [processandoFuro, setProcessandoFuro] = useState(false);
  const [processandoExclusao, setProcessandoExclusao] = useState(false);
  const toast = useToast();
  const { onCopy: copyCodGlobal, hasCopied: copiedCodGlobal } = useClipboard(item.codigo_global || '');
  const { onCopy: copyCodForn, hasCopied: copiedCodForn } = useClipboard(item.codigo_fornecedor || '');

  const divergencias = item.divergencias || [];
  const respostas = item.respostas || [];
  const ajustesPendentes = divergencias.filter(d => TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
  const divCadastrais = divergencias.filter(d => !TIPOS_AJUSTE.includes(d.tipo));

  // Identifica resposta "original" como a que tem mesmo código + marca do solicitado
  const respostaOriginal = respostas.find(r =>
    r.codigo_fornecedor === (item.codigo_fornecedor || item.codigo_global) &&
    (r.marca_oferecida || '').toLowerCase() === (item.marca_solicitada || '(sem marca)').toLowerCase()
  );
  const alternativas = respostas.filter(r => r.id !== respostaOriginal?.id);

  // Para item com furo, resposta aprovada = alternativa aprovada (muda o efeito
  // do botão "Excluir da lista de cotação")
  const temAlternativaAprovadaResp = respostas.some(r => r.status_aprovacao === 'aprovado');

  // Ajuste só vira aprovável após o fornecedor confirmar o item. Sem confirmação,
  // o comprador apenas aguarda — não vê o valor ajustado nem pode aprovar.
  const ajusteEhAprovavel = ajustesPendentes.length > 0 && !!respostaOriginal;
  const ajusteAguardandoConfirmacao = ajustesPendentes.length > 0 && !respostaOriginal;

  const estado = item.estado_calculado || null;
  const estadoInfo = estado ? ESTADO_LABELS[estado] : null;

  const produtoAdaptado = {
    id: item.produto_id,
    nome: item.nome_produto,
    fornecedor,
    codigo_fornecedor: item.codigo_fornecedor,
    codigo_fabricante: item.codigo_global,
    img: item.img || null,
  };

  const handleAceitarAjuste = async () => {
    if (ajustesPendentes.length === 0) return;
    setProcessandoAjuste(true);
    const r = await onAprovarAjuste(ajustesPendentes[0].id);
    setProcessandoAjuste(false);
    if (r) {
      toast({ title: "Ajuste aprovado e item confirmado", status: "success", duration: 2500 });
      onRefresh();
    } else {
      toast({ title: "Erro ao aprovar ajuste", status: "error", duration: 2500 });
    }
  };

  const handleRejeitarAjuste = async () => {
    setProcessandoAjuste(true);
    for (const aj of ajustesPendentes) {
      await onTratarDivergencia(aj.id, 'dispensar', 'Ajuste rejeitado pelo comprador');
    }
    setProcessandoAjuste(false);
    onRefresh();
  };

  const handleAceitarFuroLocal = async () => {
    setProcessandoFuro(true);
    await onAceitarFuro(item.id);
    setProcessandoFuro(false);
  };

  const handleDesfazerAceiteFuroLocal = async () => {
    setProcessandoFuro(true);
    await onDesfazerAceiteFuro(item.id);
    setProcessandoFuro(false);
  };

  const handleToggleExclusaoCotacao = async () => {
    setProcessandoExclusao(true);
    if (item.excluido_cotacao) {
      await onDesfazerExclusaoCotacao(item.id);
    } else {
      await onExcluirCotacao(item.id);
    }
    setProcessandoExclusao(false);
  };

  const corBorda = item.tem_furo_estoque
    ? '#DC2626'
    : ajusteEhAprovavel
      ? '#F97316'
      : divCadastrais.some(d => d.status === 'pendente')
        ? '#EAB308'
        : respostaOriginal && respostaOriginal.status_aprovacao === 'aprovado'
          ? '#16A34A'
          : '#E2E8F0';

  return (
    <Card mb={3} boxShadow="md" borderTop="4px solid" borderTopColor={corBorda}>
      <CardBody p={4}>
        {/* Header do item */}
        <HStack align="start" spacing={3} mb={3}>
          <Box cursor="pointer" onClick={imageMd.onOpen} _hover={{ transform: "scale(1.05)" }}>
            <ProdutoImagem produto={produtoAdaptado} width="64px" height="64px" imagemAPI={item.img} />
          </Box>
          <VStack align="start" spacing={1} flex={1}>
            <HStack flexWrap="wrap" spacing={2}>
              <Heading size="sm" color="#01695E">{item.nome_produto}</Heading>
              {item.urgente && <Badge colorScheme="orange">Urgente</Badge>}
              {estadoInfo && (
                <Badge colorScheme={estadoInfo.color} fontSize="xs" px={2} py={1}>
                  {estadoInfo.label}
                </Badge>
              )}
            </HStack>
            <HStack flexWrap="wrap" spacing={3} fontSize="xs" color="gray.700">
              {item.marca_solicitada && (
                <HStack spacing={1}><FiTag size={10} /><Text><strong>{item.marca_solicitada}</strong></Text></HStack>
              )}
              {item.codigo_global && (
                <Tooltip label={copiedCodGlobal ? "Copiado!" : "Copiar cód. global"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodGlobal ? "green" : "teal"}
                    leftIcon={<CopyIcon />} onClick={copyCodGlobal} px={1} h="auto">
                    {item.codigo_global}
                  </Button>
                </Tooltip>
              )}
              {item.codigo_fornecedor && item.codigo_fornecedor !== item.codigo_global && (
                <Tooltip label={copiedCodForn ? "Copiado!" : "Copiar cód. fornecedor"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodForn ? "green" : "blue"}
                    leftIcon={<CopyIcon />} onClick={copyCodForn} px={1} h="auto">
                    {item.codigo_fornecedor}
                  </Button>
                </Tooltip>
              )}
            </HStack>
            <HStack spacing={3} fontSize="xs" color="gray.700">
              <HStack spacing={1}><FiPackage size={10} /><Text>Pedido: <strong>{item.quantidade_solicitada} un.</strong></Text></HStack>
              {item.preco_referencia && (
                <HStack spacing={1}><FiDollarSign size={10} /><Text>Ref.: <strong>{formatarMoedaDecimal(item.preco_referencia)}</strong></Text></HStack>
              )}
              {item.preco_maximo && (
                <Text color="red.500">Máx.: {formatarMoedaDecimal(item.preco_maximo)}</Text>
              )}
            </HStack>
            {item.observacoes && (
              <Text fontSize="xs" color="blue.700" bg="blue.50" px={2} py={1} borderRadius="sm">
                📝 {item.observacoes}
              </Text>
            )}
          </VStack>
        </HStack>

        <VStack align="stretch" spacing={3}>
          {/* Ajuste pendente (qtd/preço) — só aprovável após o fornecedor confirmar */}
          {ajusteEhAprovavel && (
            <AjusteCard
              item={item}
              ajustes={ajustesPendentes}
              onAceitar={handleAceitarAjuste}
              onRejeitar={handleRejeitarAjuste}
              processando={processandoAjuste}
            />
          )}

          {/* Fornecedor ajustou mas ainda não confirmou — aprovação bloqueada */}
          {ajusteAguardandoConfirmacao && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                O fornecedor ainda está preenchendo este item. A aprovação será
                liberada quando ele confirmar.
              </Text>
            </Alert>
          )}

          {/* Furo declarado */}
          {item.tem_furo_estoque && (
            <Box bg="red.50" borderWidth="2px" borderColor="red.400" borderRadius="md" p={3}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <VStack align="start" spacing={1}>
                  <HStack><FiAlertTriangle color="#DC2626" />
                    <Text fontSize="sm" fontWeight="bold" color="red.800">FORNECEDOR DECLAROU SEM ESTOQUE</Text>
                  </HStack>
                  {item.furo_declarado_em && (
                    <Text fontSize="xs" color="gray.600">
                      Declarado em {new Date(item.furo_declarado_em).toLocaleString('pt-BR')}
                    </Text>
                  )}
                  {item.furo_aceito_em && (
                    <Text fontSize="xs" color="green.700" fontWeight="bold">
                      ✓ Furo aceito formalmente em {new Date(item.furo_aceito_em).toLocaleString('pt-BR')}
                    </Text>
                  )}
                  {item.excluido_cotacao && (
                    <Text fontSize="xs" color="red.700" fontWeight="bold">
                      🚫 Fora da recotação desde {item.excluido_cotacao_em ? new Date(item.excluido_cotacao_em).toLocaleString('pt-BR') : '-'}
                      {' '}por {item.excluido_cotacao_por?.name || 'Sistema (automático)'}
                      {' '}— não volta a entrar em novas listas de compras
                    </Text>
                  )}
                </VStack>
                <VStack align="end" spacing={2}>
                  {!item.excluido_cotacao && (
                    !item.furo_aceito_em ? (
                      <Button size="sm" colorScheme="red" leftIcon={<FiCheck />}
                        onClick={handleAceitarFuroLocal} isLoading={processandoFuro} loadingText="Aceitando...">
                        Aceitar furo
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FiX />}
                        onClick={handleDesfazerAceiteFuroLocal} isLoading={processandoFuro} loadingText="Desfazendo...">
                        Desfazer aceite
                      </Button>
                    )
                  )}
                  <Tooltip
                    label={item.excluido_cotacao
                      ? 'Reverter: o item volta a ser elegível para novas listas de compras'
                      : (temAlternativaAprovadaResp
                        ? 'Tira o item do ciclo de recotação (a venda segue atendida pela alternativa aprovada)'
                        : 'Assume o furo e tira o item do ciclo de recotação: ele não volta a entrar em novas listas de compras. A venda segue sendo cotada em outros fornecedores.')}
                    placement="top" hasArrow>
                    <Button size="sm"
                      colorScheme={item.excluido_cotacao ? 'red' : 'gray'}
                      variant={item.excluido_cotacao ? 'solid' : 'outline'}
                      leftIcon={<FiSlash />}
                      onClick={handleToggleExclusaoCotacao}
                      isLoading={processandoExclusao}>
                      {item.excluido_cotacao ? 'Fora da recotação — reverter' : 'Não recotar este item'}
                    </Button>
                  </Tooltip>
                </VStack>
              </Flex>
            </Box>
          )}

          {/* Divergências cadastrais */}
          {divCadastrais.map(d => (
            <DivergenciaCadastralCard
              key={d.id}
              divergencia={d}
              item={item}
              onTratar={onTratarDivergencia}
            />
          ))}

          {/* Confirmação original (resposta com mesmos código + marca).
              Esconde quando há ajuste pendente — o AjusteCard consolidado já cuida da aprovação. */}
          {respostaOriginal && ajustesPendentes.length === 0 && (
            <RespostaCard
              resposta={respostaOriginal}
              item={item}
              ehOriginal={true}
              onAprovar={onAprovarResposta}
              onRejeitar={onRejeitarResposta}
              onRefresh={onRefresh}
              onDesfazerAprovacao={onDesfazerAprovacao}
              onMarcarFuroPos={onMarcarFuroPos}
            />
          )}

          {/* Alternativas */}
          {alternativas.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={1}>
                ALTERNATIVAS OFERECIDAS ({alternativas.length})
              </Text>
              <VStack align="stretch" spacing={2}>
                {alternativas.map(r => (
                  <RespostaCard
                    key={r.id}
                    resposta={r}
                    item={item}
                    ehOriginal={false}
                    onAprovar={onAprovarResposta}
                    onRejeitar={onRejeitarResposta}
                    onRefresh={onRefresh}
                    onDesfazerAprovacao={onDesfazerAprovacao}
                    onMarcarFuroPos={onMarcarFuroPos}
                  />
                ))}
              </VStack>
            </Box>
          )}

          {/* Sem nenhuma resposta nem furo */}
          {!respostaOriginal && alternativas.length === 0 && !item.tem_furo_estoque && ajustesPendentes.length === 0 && (
            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon />
              <Text fontSize="sm">Aguardando resposta do fornecedor</Text>
            </Alert>
          )}
        </VStack>
      </CardBody>

      <Modal isOpen={imageMd.isOpen} onClose={imageMd.onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
          <ModalCloseButton color="white" bg="blackAlpha.600" size="lg" top={2} right={2} />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            <ProdutoImagem produto={produtoAdaptado} width="auto" height="auto" maxW="100%" maxH="80vh" imagemAPI={item.img} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
}

// ─── Componente principal ───────────────────────────────────────────────
// Serve a rota /listas-compras/:id e tambem o modal do Kanban de compras — no
// modal o id vem por prop e quem fecha a tela e o proprio modal, nao o navigate.
export default function ListaCompraDetalhes({ listaId = null, emModal = false, onFechar = null }) {
  const { id: idRota } = useParams();
  const id = listaId ?? idRota;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Quem chega pelo Kanban (link com ?origem=kanban) volta pra la, nao pra tabela.
  const voltarPara = searchParams.get('origem') === 'kanban'
    ? '/listas-compras/kanban'
    : '/listas-compras';

  const [loading, setLoading] = useState(true);
  const [lista, setLista] = useState(null);
  const [finalizando, setFinalizando] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [acaoMassa, setAcaoMassa] = useState(false);

  useEffect(() => { fetchDetalhes(); /* eslint-disable-next-line */ }, [id]);

  const fetchDetalhes = async () => {
    setLoading(true);
    const data = await getListaCompraDetalhes(id);
    if (data) setLista(data);
    else toast({ title: "Erro ao carregar", status: "error", duration: 3000 });
    setLoading(false);
  };

  const handleAprovarResposta = async (respostaId, qtd, obs) => {
    const r = await aprovarRespostaFornecedor({
      resposta_id: respostaId, acao: "aprovar",
      quantidade_aprovada: parseInt(qtd, 10), observacoes_aprovacao: obs,
    });
    toast({ title: r ? "Aprovada!" : "Erro ao aprovar", status: r ? "success" : "error", duration: 2500 });
    if (r) fetchDetalhes();
  };

  const handleRejeitarResposta = async (respostaId, obs) => {
    const r = await aprovarRespostaFornecedor({
      resposta_id: respostaId, acao: "rejeitar", observacoes_aprovacao: obs,
    });
    toast({ title: r ? "Rejeitada" : "Erro ao rejeitar", status: r ? "info" : "error", duration: 2500 });
    if (r) fetchDetalhes();
  };

  const handleAceitarFuro = async (itemId) => {
    const r = await aceitarFuroEstoqueItem(itemId);
    const ok = r && !r.erro;
    if (ok) {
      toast({ title: "Furo aceito", status: "success", duration: 2500 });
      fetchDetalhes();
    } else {
      toast({
        title: "Erro ao aceitar furo",
        description: r?.message || "Nada foi salvo. Tente novamente.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
    return ok;
  };

  const handleDesfazerAceiteFuro = async (itemId) => {
    const r = await desfazerAceiteFuroEstoqueItem(itemId);
    toast({ title: r ? "Aceite desfeito" : "Erro", status: r ? "info" : "error", duration: 2500 });
    if (r) fetchDetalhes();
    return r;
  };

  const handleExcluirCotacao = async (itemId) => {
    const r = await excluirItemCotacao(itemId);
    const ok = r && !r.erro;
    if (ok) {
      toast({ title: "Item fora da recotação", status: "warning", duration: 3000 });
      fetchDetalhes();
    } else {
      toast({
        title: "Erro ao tirar da recotação",
        description: r?.message || "Nada foi salvo. Tente novamente.",
        status: "error", duration: 6000, isClosable: true,
      });
    }
    return ok;
  };

  const handleDesfazerExclusaoCotacao = async (itemId) => {
    const r = await desfazerExclusaoItemCotacao(itemId);
    const ok = r && !r.erro;
    if (ok) {
      toast({ title: "Exclusão desfeita — item volta ao ciclo de recotação", status: "info", duration: 3000 });
      fetchDetalhes();
    } else {
      toast({
        title: "Erro ao desfazer exclusão",
        description: r?.message || "Nada foi salvo. Tente novamente.",
        status: "error", duration: 6000, isClosable: true,
      });
    }
    return ok;
  };

  const handleTratarDivergencia = async (divId, acao, obs) => {
    const r = await tratarDivergencia(divId, acao, obs);
    toast({
      title: r ? (acao === 'reconhecer' ? "Reconhecida" : "Dispensada") : "Erro",
      status: r ? "success" : "error", duration: 2000,
    });
    if (r) fetchDetalhes();
    return r;
  };

  const handleAprovarAjuste = async (divId) => {
    return await aprovarAjusteDivergencia(divId);
  };

  const handleDesfazerAprovacaoResposta = async (respostaId) => {
    return await desfazerAprovacaoResposta(respostaId);
  };

  const handleMarcarFuroPos = async (itemId, observacoes) => {
    return await marcarFuroPosConfirmacaoItem(itemId, observacoes);
  };

  const handleAceitarTodosAjustes = async () => {
    if (!lista?.itens) return;
    setAcaoMassa(true);
    let ok = 0, err = 0;
    for (const item of lista.itens) {
      if (!ajusteAprovavel(item)) continue;
      const ajustes = (item.divergencias || []).filter(d => TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
      const r = await aprovarAjusteDivergencia(ajustes[0].id);
      if (r) ok++; else err++;
    }
    setAcaoMassa(false);
    toast({ title: `Ajustes aceitos: ${ok}${err ? ` • erros: ${err}` : ''}`, status: ok > 0 ? "success" : "error", duration: 3000 });
    fetchDetalhes();
  };

  const handleAprovarConfirmacoesOriginais = async () => {
    if (!lista?.itens) return;
    setAcaoMassa(true);
    let ok = 0, err = 0;
    for (const item of lista.itens) {
      const temAjustePend = (item.divergencias || []).some(d => TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
      const temDivCadPend = (item.divergencias || []).some(d => !TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
      if (temAjustePend || temDivCadPend) continue;

      const respostaOriginal = (item.respostas || []).find(r =>
        r.codigo_fornecedor === (item.codigo_fornecedor || item.codigo_global) &&
        (r.marca_oferecida || '').toLowerCase() === (item.marca_solicitada || '(sem marca)').toLowerCase() &&
        r.status_aprovacao === 'pendente'
      );
      if (!respostaOriginal) continue;
      const r = await aprovarRespostaFornecedor({
        resposta_id: respostaOriginal.id, acao: "aprovar",
        quantidade_aprovada: parseInt(respostaOriginal.quantidade_disponivel, 10),
        observacoes_aprovacao: "Aprovação em massa — item confirmado",
      });
      if (r) ok++; else err++;
    }
    setAcaoMassa(false);
    toast({ title: `Confirmações aprovadas: ${ok}${err ? ` • erros: ${err}` : ''}`, status: ok > 0 ? "success" : "error", duration: 3000 });
    fetchDetalhes();
  };

  const handleFinalizar = async () => {
    setFinalizando(true);
    const r = await finalizarAprovacaoLista(id);
    setFinalizando(false);
    if (r && !r.erro) {
      toast({ title: "Lista aprovada e finalizada!", status: "success", duration: 4000 });
      if (emModal) onFechar?.(); else navigate(voltarPara);
    } else if (r?.erro && r?.itens_pendentes) {
      const nomes = r.itens_pendentes.map(i => `• ${i.nome_produto} (${ESTADO_LABELS[i.estado_calculado]?.label || i.estado_calculado})`).join('\n');
      toast({ title: "Itens pendentes bloqueiam finalização", description: nomes, status: "error", duration: 7000 });
    } else {
      toast({ title: "Erro ao finalizar", status: "error", duration: 3000 });
    }
  };

  const kpis = useMemo(() => {
    const itens = lista?.itens || [];
    let aprovados = 0, ajustes = 0, alternativas = 0, divCadastrais = 0, furos = 0, totalAprovado = 0;
    let confirmacoesOriginaisPendentes = 0;
    itens.forEach(i => {
      const respApr = (i.respostas || []).filter(r => r.status_aprovacao === 'aprovado');
      if (respApr.length > 0) {
        aprovados++;
        totalAprovado += respApr.reduce((s, r) => s + (parseFloat(r.preco_unitario || 0) * parseInt(r.quantidade_aprovada || 0, 10)), 0);
      }
      const temAjustePend = ajusteAprovavel(i);
      const temDivCadPend = (i.divergencias || []).some(d => !TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
      if (temAjustePend) ajustes++;
      if (temAlternativaOferecida(i)) alternativas++;
      if (temDivCadPend) divCadastrais++;
      if (i.tem_furo_estoque) furos++;

      const respostaOriginal = (i.respostas || []).find(r =>
        r.codigo_fornecedor === (i.codigo_fornecedor || i.codigo_global) &&
        (r.marca_oferecida || '').toLowerCase() === (i.marca_solicitada || '(sem marca)').toLowerCase()
      );
      if (respostaOriginal && respostaOriginal.status_aprovacao === 'pendente' && !temAjustePend && !temDivCadPend) {
        confirmacoesOriginaisPendentes++;
      }
    });
    return { total: itens.length, aprovados, ajustes, alternativas, divCadastrais, furos, totalAprovado, confirmacoesOriginaisPendentes };
  }, [lista]);

  const itensFiltrados = useMemo(() => {
    if (!lista?.itens) return [];
    return lista.itens.filter(i => {
      const ajustePend = ajusteAprovavel(i);
      const altPend = (i.respostas || []).some(r => r.status_aprovacao === 'pendente');
      const divCad = (i.divergencias || []).some(d => !TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente');
      const aprov = (i.respostas || []).some(r => r.status_aprovacao === 'aprovado');

      switch (filtro) {
        case 'pendentes':    return ajustePend || altPend;
        case 'ajustes':      return ajustePend;
        case 'alternativas': return temAlternativaOferecida(i);
        case 'divergencias': return divCad;
        case 'furos':        return i.tem_furo_estoque;
        case 'aprovados':    return aprov;
        default:             return true;
      }
    });
  }, [lista, filtro]);

  if (loading) {
    return (
      <Box minH={emModal ? "300px" : "100vh"} display="flex" justifyContent="center" alignItems="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="#F68633" thickness="4px" />
          <Text color="#01695E">Carregando detalhes...</Text>
        </VStack>
      </Box>
    );
  }

  if (!lista) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />Lista não encontrada
        </Alert>
      </Container>
    );
  }

  const fornecedor = lista.fornecedor?.pessoa?.nome || `Fornecedor #${lista.fornecedor_id}`;
  const linkLista = lista.url_enviada
    || (lista.hash ? `https://admin.tagaautopartes.com.br/lista-compras/${lista.hash}` : null);

  const itensNaoTerminais = (lista.itens || []).filter(i => {
    const e = i.estado_calculado;
    if (e) return ESTADOS_BLOQUEADORES.includes(e);
    return (i.respostas || []).some(r => r.status_aprovacao === 'pendente');
  });
  const podeFinalizar = itensNaoTerminais.length === 0;

  const divergenciasNaoTratadas = (lista.itens || []).reduce((acc, i) =>
    acc + (i.divergencias || []).filter(d => !TIPOS_AJUSTE.includes(d.tipo) && d.status === 'pendente').length, 0);

  // Furos sem alternativa aprovada ainda não aceitos — serão aceitos
  // automaticamente no ato da finalização
  const furosParaAceitar = (lista.itens || []).filter(i =>
    i.tem_furo_estoque && !i.furo_aceito_em &&
    !(i.respostas || []).some(r => r.status_aprovacao === 'aprovado')
  ).length;

  return (
    // No modal a moldura (altura cheia, faixa da sidebar, fundo) e do proprio modal.
    <Box
      minH={emModal ? undefined : "100vh"}
      bg={emModal ? undefined : "#f0f0f0"}
      ml={emModal ? 0 : { base: 0, md: "64px" }}
      pt={emModal ? 0 : { base: "60px", md: 4 }}
      pb={emModal ? 0 : 4}
    >
      <Container maxW="container.xl" px={emModal ? 0 : undefined}>
        {/* Header — no modal o cabecalho e o botao de fechar sao do modal */}
        {!emModal && (
          <HStack mb={4} justifyContent="space-between" flexWrap="wrap">
            <HStack>
              <IconButton icon={<FiArrowLeft />} onClick={() => navigate(voltarPara)} variant="ghost" aria-label="Voltar" />
              <Heading size="lg" color="#01695E">Conferência de cotação</Heading>
            </HStack>
          </HStack>
        )}

        {/* Card identificação */}
        <Card mb={4} boxShadow="lg" bg="#F68633" color="white">
          <CardBody>
            <HStack spacing={4} flexWrap="wrap">
              <Heading size="md">{fornecedor}</Heading>
              <Badge bg="whiteAlpha.300" color="white" fontSize="md" px={3} py={1}>
                {new Date(lista.data_lista).toLocaleDateString('pt-BR')}
              </Badge>
              <Badge colorScheme={lista.status_resposta === 'respondido' ? 'green' : 'gray'} fontSize="sm">
                {lista.status_resposta}
              </Badge>
            </HStack>
            {lista.observacoes && (
              <Box bg="whiteAlpha.300" p={3} borderRadius="md" mt={2}>
                <Text fontSize="sm" fontWeight="bold">Observações TAGA:</Text>
                <Text fontSize="sm">{lista.observacoes}</Text>
              </Box>
            )}
            {lista.observacoes_fornecedor && (
              <Box bg="whiteAlpha.400" p={3} borderRadius="md" mt={2}>
                <Text fontSize="sm" fontWeight="bold">Observações do fornecedor:</Text>
                <Text fontSize="sm">{lista.observacoes_fornecedor}</Text>
              </Box>
            )}
            <LinkListaEnviada url={linkLista} />
          </CardBody>
        </Card>

        {/* Finalização automática (#1950) */}
        {!!lista.finalizada_automaticamente && (
          <Alert status="success" borderRadius="md" mb={4} alignItems="flex-start">
            <AlertIcon />
            <Box>
              <Text fontSize="sm" fontWeight="bold">
                🤖 Lista aprovada e finalizada automaticamente pelo sistema
                {lista.aprovado_em && ` em ${new Date(lista.aprovado_em).toLocaleString('pt-BR')}`}
              </Text>
              <Text fontSize="xs">
                Todos os itens estavam resolvidos sem pontos de decisão pendentes.
                Nenhuma ação é necessária da sua parte.
              </Text>
            </Box>
          </Alert>
        )}

        {/* KPIs */}
        <Card mb={4} boxShadow="md">
          <CardBody py={3}>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={3}>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Total</StatLabel>
                <StatNumber fontSize="2xl" color="#01695E">{kpis.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Aprovados</StatLabel>
                <StatNumber fontSize="2xl" color="green.600">{kpis.aprovados}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Ajustes pendentes</StatLabel>
                <StatNumber fontSize="2xl" color="orange.500">{kpis.ajustes}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Alternativas</StatLabel>
                <StatNumber fontSize="2xl" color="blue.500">{kpis.alternativas}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Divergências</StatLabel>
                <StatNumber fontSize="2xl" color="yellow.600">{kpis.divCadastrais}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs" color="gray.600">Sem estoque</StatLabel>
                <StatNumber fontSize="2xl" color="red.500">{kpis.furos}</StatNumber>
              </Stat>
            </SimpleGrid>
            <Divider my={3} />
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">Total estimado (aprovados):</Text>
              <Text fontSize="xl" fontWeight="bold" color="#01695E">
                {formatarMoedaDecimal(kpis.totalAprovado)}
              </Text>
            </HStack>
          </CardBody>
        </Card>

        {/* Filtros */}
        <Card mb={4} boxShadow="sm">
          <CardBody py={3}>
            <ButtonGroup size="sm" isAttached variant="outline" flexWrap="wrap" gap={1}>
              {FILTROS.map(f => (
                <Button
                  key={f.key}
                  colorScheme={filtro === f.key ? "teal" : "gray"}
                  variant={filtro === f.key ? "solid" : "outline"}
                  bg={filtro === f.key ? "#01695E" : undefined}
                  onClick={() => setFiltro(f.key)}
                >
                  {f.label}
                </Button>
              ))}
            </ButtonGroup>
          </CardBody>
        </Card>

        {/* Ações em massa */}
        {(kpis.ajustes > 0 || kpis.confirmacoesOriginaisPendentes > 0) && (
          <Card mb={4} boxShadow="sm" borderTop="3px solid #01695E">
            <CardBody py={3}>
              <HStack flexWrap="wrap" spacing={3}>
                {kpis.ajustes > 0 && (
                  <Button
                    colorScheme="orange" leftIcon={<FiCheckCircle />}
                    onClick={handleAceitarTodosAjustes} isLoading={acaoMassa}
                    size="sm"
                  >
                    Aceitar todos os ajustes ({kpis.ajustes})
                  </Button>
                )}
                {kpis.confirmacoesOriginaisPendentes > 0 && (
                  <Button
                    colorScheme="green" leftIcon={<FiCheckCircle />}
                    onClick={handleAprovarConfirmacoesOriginais} isLoading={acaoMassa}
                    size="sm"
                  >
                    Aprovar confirmações sem divergência ({kpis.confirmacoesOriginaisPendentes})
                  </Button>
                )}
              </HStack>
            </CardBody>
          </Card>
        )}

        {/* Cards de itens */}
        {itensFiltrados.length > 0 ? (
          itensFiltrados.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              fornecedor={fornecedor}
              onAprovarResposta={handleAprovarResposta}
              onRejeitarResposta={handleRejeitarResposta}
              onRefresh={fetchDetalhes}
              onAceitarFuro={handleAceitarFuro}
              onDesfazerAceiteFuro={handleDesfazerAceiteFuro}
              onExcluirCotacao={handleExcluirCotacao}
              onDesfazerExclusaoCotacao={handleDesfazerExclusaoCotacao}
              onTratarDivergencia={handleTratarDivergencia}
              onAprovarAjuste={handleAprovarAjuste}
              onDesfazerAprovacao={handleDesfazerAprovacaoResposta}
              onMarcarFuroPos={handleMarcarFuroPos}
            />
          ))
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon />Nenhum item para o filtro selecionado.
          </Alert>
        )}

        {/* Finalizar */}
        {lista.status_resposta !== 'aprovado' && lista.status_resposta !== 'finalizado' && lista.status_resposta !== 'retirado' && (
          <Card mt={6} boxShadow="lg" borderTop="4px solid #01695E">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" color="#01695E">Finalizar aprovação</Heading>
                <Text fontSize="sm" color="gray.600">
                  Ao finalizar, a lista é marcada como aprovada e o fornecedor é notificado via WhatsApp.
                </Text>

                {!podeFinalizar ? (
                  <Alert status="error" borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="bold">
                        {itensNaoTerminais.length} {itensNaoTerminais.length === 1 ? 'item bloqueia' : 'itens bloqueiam'} a finalização
                      </Text>
                      <Text fontSize="xs" mb={2}>Resolva (aceite ajuste, aprove resposta, aceite furo):</Text>
                      <VStack align="start" spacing={1} maxH="200px" overflowY="auto">
                        {itensNaoTerminais.slice(0, 10).map(i => (
                          <Text key={i.id} fontSize="xs">
                            • <strong>{i.nome_produto}</strong> — {ESTADO_LABELS[i.estado_calculado]?.label || i.estado_calculado}
                          </Text>
                        ))}
                        {itensNaoTerminais.length > 10 && (
                          <Text fontSize="xs" fontStyle="italic">… e mais {itensNaoTerminais.length - 10}</Text>
                        )}
                      </VStack>
                    </Box>
                  </Alert>
                ) : (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm" fontWeight="bold">Tudo pronto para finalizar.</Text>
                  </Alert>
                )}

                {furosParaAceitar > 0 && (
                  <Alert status="warning" borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <Box>
                      <Text fontSize="sm" fontWeight="bold">
                        {furosParaAceitar} {furosParaAceitar === 1 ? 'furo sem alternativa será aceito' : 'furos sem alternativa serão aceitos'} ao finalizar
                      </Text>
                      <Text fontSize="xs">
                        As vendas vinculadas ficam marcadas como furo e seguem para cotação
                        em outros fornecedores. Depois de finalizar, você pode desfazer o
                        aceite item a item.
                      </Text>
                    </Box>
                  </Alert>
                )}

                {divergenciasNaoTratadas > 0 && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontSize="sm" fontWeight="bold">
                        {divergenciasNaoTratadas} divergência(s) cadastral(is) não tratada(s)
                      </Text>
                      <Text fontSize="xs">Não bloqueia, mas é recomendado reconhecer ou dispensar antes.</Text>
                    </Box>
                  </Alert>
                )}

                <Button
                  colorScheme="green" bg="#01695E" size="lg"
                  leftIcon={<FiCheckCircle />}
                  onClick={handleFinalizar} isLoading={finalizando} loadingText="Finalizando..."
                  isDisabled={!podeFinalizar}
                >
                  Finalizar aprovação
                </Button>
              </VStack>
            </CardBody>
          </Card>
        )}
      </Container>
    </Box>
  );
}
